import { createSlice } from '@reduxjs/toolkit';
import authService from '../../utils/authService';

// Récupérer l'utilisateur une seule fois pour éviter les appels multiples
const currentUser = authService.getCurrentUser();

const initialState = {
  user: currentUser,
  isAuthenticated: authService.isAuthenticated(),
  userType: currentUser?.role || null,
  loading: false,
  error: null,
  registrationSuccess: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.userType = action.payload.user.role;
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    registerStart: (state) => {
      state.loading = true;
      state.error = null;
      state.registrationSuccess = false;
    },
    registerProfessionalSuccess: (state, action) => {
      state.loading = false;
      state.registrationSuccess = true;
      state.user = action.payload;
      state.userType = 'professional';
      state.isAuthenticated = true;
    },
    registerOrganizationSuccess: (state, action) => {
      state.loading = false;
      state.registrationSuccess = true;
      state.user = action.payload;
      state.userType = 'organization';
      state.isAuthenticated = true;
    },
    registerFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
      state.registrationSuccess = false;
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.userType = null;
      state.error = null;
      state.registrationSuccess = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearRegistrationSuccess: (state) => {
      state.registrationSuccess = false;
    },
  },
});

// Actions asynchrones pour l'authentification
export const registerUser = (userData) => async (dispatch) => {
  dispatch(registerStart());
  
  try {
    const response = await authService.register(userData);
    
    if (userData.userType === 'professional') {
      dispatch(registerProfessionalSuccess(response.data.user));
    } else if (userData.userType === 'organization') {
      dispatch(registerOrganizationSuccess(response.data.user));
    }
  } catch (error) {
    dispatch(registerFailure(error.message));
  }
};

export const loginUser = (credentials) => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    const response = await authService.login(credentials);
    dispatch(loginSuccess({
      user: response.data.user
    }));
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

export const logoutUser = () => async (dispatch) => {
  try {
    await authService.logout();
    dispatch(logout());
  } catch (error) {
    // Même en cas d'erreur, on déconnecte localement
    dispatch(logout());
  }
};

export const getCurrentUser = () => async (dispatch) => {
  // Ne pas déclencher loginStart() pour éviter les changements d'état inutiles
  try {
    const response = await authService.getMe();
    dispatch(loginSuccess({
      user: response.data.user
    }));
  } catch (error) {
    dispatch(loginFailure(error.message));
    // Si l'utilisateur n'est pas authentifié, on nettoie le localStorage
    authService.logout();
  }
};

export const updateUserDetails = (userData) => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    const response = await authService.updateDetails(userData);
    dispatch(loginSuccess({
      user: response.data.user
    }));
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

export const updateUserPassword = (passwordData) => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    await authService.updatePassword(passwordData);
    // Pas besoin de mettre à jour l'utilisateur pour un changement de mot de passe
    dispatch(clearError());
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

export const forgotPassword = (email) => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    await authService.forgotPassword(email);
    dispatch(clearError());
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

export const resetPassword = (resetToken, password) => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    const response = await authService.resetPassword(resetToken, password);
    dispatch(loginSuccess({
      user: response.data.user
    }));
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

export const verifyEmail = (token) => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    const response = await authService.verifyEmail(token);
    dispatch(loginSuccess({
      user: response.data.user
    }));
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

export const verifyPhone = (verificationData) => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    const response = await authService.verifyPhone(verificationData);
    dispatch(loginSuccess({
      user: response.data.user
    }));
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

export const fetchUserProfile = () => async (dispatch) => {
  dispatch(loginStart());
  
  try {
    const response = await authService.getMe();
    const userData = response.data.data.user || response.data.data;
    dispatch(loginSuccess({
      user: userData
    }));
    // Update localStorage with fresh data
    localStorage.setItem('user', JSON.stringify(userData));
  } catch (error) {
    dispatch(loginFailure(error.message));
  }
};

// Fonctions de compatibilité pour l'ancien code
export const registerProfessional = (professionalData) => async (dispatch) => {
  const userData = { ...professionalData, userType: 'professional' };
  return dispatch(registerUser(userData));
};

export const registerOrganization = (organizationData) => async (dispatch) => {
  dispatch(registerStart());
  
  try {
    const response = await authService.registerOrganization(organizationData);
    // Utiliser response.data.user pour avoir l'objet utilisateur avec le rôle correct
    // Au lieu de response.data.organization qui n'a pas la propriété role='organization'
    dispatch(registerOrganizationSuccess(response.data.user));
  } catch (error) {
    dispatch(registerFailure(error.message));
  }
};

export const { 
  loginStart, 
  loginSuccess, 
  loginFailure, 
  registerStart,
  registerProfessionalSuccess,
  registerOrganizationSuccess,
  registerFailure,
  logout, 
  clearError,
  clearRegistrationSuccess
} = authSlice.actions;

export default authSlice.reducer;